import React, { useEffect, useMemo, useRef, useState } from "react";
import PaginationControls from "./PaginationControls";

type PaginatorProps<T> = {
    data: T[];
    pageSize: number;
    pageSizeOptions?: number[];
    searchDeps?: any[];
    onPaginatedDataChange: (data: T[]) => void;
};

const Paginator = <T,>({
    data,
    pageSize,
    pageSizeOptions = [10, 25, 50, 100],
    searchDeps = [],
    onPaginatedDataChange,
}: PaginatorProps<T>): React.ReactElement => {
    const [currentPageSize, setCurrentPageSize] = useState(pageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.max(1, Math.ceil(data.length / currentPageSize));

    const prevDataRef = useRef<string>(""); // serialized for shallow compare

    // Reset page when filters/search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [...searchDeps]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * currentPageSize;
        const slice = data.slice(start, start + currentPageSize);

        return slice;
    }, [data, currentPage, currentPageSize]);

    useEffect(() => {
        const serialized = JSON.stringify(paginatedData);
        if (prevDataRef.current !== serialized) {
            prevDataRef.current = serialized;
            onPaginatedDataChange(paginatedData);
        }
    }, [paginatedData]);

    const goToPage = (page: number) => {
        if (page >= 1 && page <= totalPages) setCurrentPage(page);
    };

    return (
        <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecords={data.length}
            pageSizeOptions={pageSizeOptions}
            pageSize={currentPageSize}
            setPageSize={(pageSize) => {
                setCurrentPage(1);
                setCurrentPageSize(pageSize)
            }}
            onPageChange={goToPage}
        />
    );
};

export default Paginator;
